/**
 * ============================================
 * PS5 / SONY DOMO ANALYTICS DASHBOARD
 * ============================================
 */

import React, { useState, useMemo, useCallback, type ReactNode } from 'react';
import {
  RefreshCw,
  Download,
  AlertTriangle,
  Gamepad2,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  CalendarDays,
  Globe,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
  Legend,
  ComposedChart,
} from 'recharts';
import { useTheme } from '@/context/ThemeContext';
import { useDateRange } from '@/context/DateRangeContext';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { TimeScaleToggle, type TimeScale } from '@/components/common/TimeScaleToggle';
import { usePS5Analytics, PS5DailyStat, type PS5AnalyticsData } from '@/hooks/usePS5Analytics';
import { CountryMultiSelect } from '@/components/common/CountryMultiSelect';
import { CountryChips } from '@/components/common/CountryChips';
import { MARKER_CHART_MARGIN, markerLabel } from '@/components/common/ChartMarkers';
import { formatAxisCount } from '@/lib/utils';
import { CardCountryStacked, CountryCard, CountryScope, useCardOverrides, useViewCache } from '@/components/common/CardCountryFilter';
import { addCountryLines, domoCountryOptions, filterDomoData, topDomoCountries, type DomoByCountry } from '@/utils/domoCountryFilter';

// ============================================
// CONSTANTS
// ============================================

const REGION_NAMES: Record<string, string> = {
  'SIEA': 'Americas',
  'SIEE': 'Europe/ME/Africa',
  'SIEAsia': 'Asia Pacific',
  'SIEJ': 'Japan',
};

// Sony's raw SIE Region codes -> the labels used on the DAU charts/tables.
const REGION_DISPLAY: Record<string, string> = {
  'SIEA': 'SIEA',
  'SIEE': 'SIEE',
  'SIEAsia': 'SIEJA-Asia',
  'SIEJ': 'SIEJA-Japan',
};

// Distinct hues for the top-15 country lines; the 4 region lines reuse the
// first four so a region keeps a stable colour across both charts.
const SERIES_COLORS = [
  '#3b82f6', '#f97316', '#10b981', '#eab308', '#ec4899',
  '#6366f1', '#22c55e', '#ef4444', '#0ea5e9', '#a855f7',
  '#14b8a6', '#f59e0b', '#64748b', '#84cc16', '#e11d48',
];
const REGION_LINE_COLORS: Record<string, string> = {
  'SIEE': '#f97316', 'SIEA': '#3b82f6', 'SIEJA-Asia': '#10b981', 'SIEJA-Japan': '#6366f1',
};

/** 'YYYY-MM' -> 'Mon' for the month-average table headers. */
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleString(undefined, { month: 'short' });
}

// ============================================
// HELPER FUNCTIONS
// ============================================

// Parse a date-only string (YYYY-MM-DD, optionally with a T... suffix) as LOCAL
// midnight. Plain `new Date('YYYY-MM-DD')` parses as UTC midnight, which — when
// compared against local-midnight baselines — causes off-by-one day errors for
// users in non-UTC timezones (the team runs on US time). Always parse local.
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = (dateStr || '').split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return new Date(NaN);
  return new Date(y, m - 1, d);
}

function filterValidData(dailyStats: PS5DailyStat[]): PS5DailyStat[] {
  if (!dailyStats || !Array.isArray(dailyStats)) return [];

  // PlayStation data has a 2-day delay, so exclude today and yesterday.
  const twoDaysAgo = new Date();
  twoDaysAgo.setHours(0, 0, 0, 0);
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

  // Only show data from 2 days ago and earlier.
  return dailyStats.filter(d => {
    if (!d || !d.Date) return false;
    return parseLocalDate(d.Date) <= twoDaysAgo;
  });
}

// Format date as dd-mmm (e.g., "03-Feb")
function formatDateDDMMM(dateStr: string): string {
  if (!dateStr) return '';
  const date = parseLocalDate(dateStr);
  if (isNaN(date.getTime())) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${day}-${month}`;
}

function calculateDAUStats(dailyStats: PS5DailyStat[], inRange?: (d?: string | null) => boolean) {
  let validData = filterValidData(dailyStats);
  // Range-aware: the summary cards must agree with the chart beside them. Without
  // this the card reported an all-time peak ("Highest DAU 16-May") while the chart
  // showed the selected window.
  if (inRange) validData = validData.filter(d => inRange(d.Date));
  if (validData.length === 0) {
    return { avg: 0, highest: 0, highestDate: '', lowest: 0, lowestDate: '' };
  }

  let total = 0, highest = 0, highestDate = '', lowest = Infinity, lowestDate = '';

  validData.forEach(d => {
    const dau = d.DAU || 0;
    total += dau;
    if (dau > highest) { highest = dau; highestDate = d.Date || ''; }
    if (dau < lowest && dau > 0) { lowest = dau; lowestDate = d.Date || ''; }
  });

  if (lowest === Infinity) lowest = 0;

  return {
    avg: Math.round(total / validData.length),
    highest,
    highestDate: highestDate ? formatDateDDMMM(highestDate) : '',
    lowest,
    lowestDate: lowestDate ? formatDateDDMMM(lowestDate) : '',
  };
}

// Filter data from Dec 15, 2025 onwards (excludes early test data with outliers)
function filterFromDec15(data: any[], dateField: string = 'Date'): any[] {
  const dec15 = parseLocalDate('2025-12-15');
  return data.filter(d => {
    if (!d || !d[dateField]) return false;
    return parseLocalDate(String(d[dateField])) >= dec15;
  });
}

// Filter data from Dec 2025 onwards
function filterFromDec2025(data: any[], dateField: string = 'Date'): any[] {
  const dec2025 = parseLocalDate('2025-12-01');
  return data.filter(d => {
    if (!d || !d[dateField]) return false;
    return parseLocalDate(String(d[dateField])) >= dec2025;
  });
}

function aggregateByTimeScale(dailyStats: PS5DailyStat[], scale: TimeScale, inRange?: (d?: string | null) => boolean) {
  // Filter valid data AND only from Dec 2025
  let validData = filterValidData(dailyStats);
  validData = filterFromDec2025(validData, 'Date');
  if (inRange) validData = validData.filter(d => inRange(d.Date));
  if (validData.length === 0) return [];

  if (scale === 'daily') {
    // Show ALL daily data from Dec 2025 (not just 30 days)
    return [...validData].reverse().map(d => ({
      date: formatDateDDMMM(d.Date || ''),
      DAU: d.DAU || 0,
      sessions: d.TotalSessions || 0,
      avgSession: d.AvgSessionMinutes || 0,
    }));
  }

  const groups: Record<string, { dau: number[]; sessions: number[]; avgSession: number[]; label: string }> = {};

  validData.forEach(d => {
    if (!d.Date) return;
    const date = parseLocalDate(d.Date);
    let key: string, label: string;

    if (scale === 'weekly') {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      key = weekStart.toISOString().split('T')[0];
      const day = weekStart.getDate().toString().padStart(2, '0');
      const month = weekStart.toLocaleDateString('en-US', { month: 'short' });
      label = `${day}-${month}`;
    } else {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }

    if (!groups[key]) groups[key] = { dau: [], sessions: [], avgSession: [], label };
    groups[key].dau.push(d.DAU || 0);
    groups[key].sessions.push(d.TotalSessions || 0);
    groups[key].avgSession.push(d.AvgSessionMinutes || 0);
  });

  // Show ALL grouped data (not limited)
  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, g]) => ({
      date: g.label,
      DAU: Math.round(g.dau.reduce((a, b) => a + b, 0) / g.dau.length),
      sessions: Math.round(g.sessions.reduce((a, b) => a + b, 0) / g.sessions.length),
      avgSession: Math.round((g.avgSession.reduce((a, b) => a + b, 0) / g.avgSession.length) * 10) / 10,
    }));
}

// Separate function for session time that filters from Dec 15 (excludes outlier test data)
function aggregateSessionByTimeScale(dailyStats: PS5DailyStat[], scale: TimeScale, inRange?: (d?: string | null) => boolean) {
  let validData = filterValidData(dailyStats);
  validData = filterFromDec15(validData, 'Date');  // Use Dec 15 to exclude outliers
  if (inRange) validData = validData.filter(d => inRange(d.Date));
  if (validData.length === 0) return [];

  if (scale === 'daily') {
    return [...validData].reverse().map(d => ({
      date: formatDateDDMMM(d.Date || ''),
      avgSession: d.AvgSessionMinutes || 0,
    }));
  }

  const groups: Record<string, { avgSession: number[]; label: string }> = {};

  validData.forEach(d => {
    if (!d.Date) return;
    const date = parseLocalDate(d.Date);
    let key: string, label: string;

    if (scale === 'weekly') {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      key = weekStart.toISOString().split('T')[0];
      const day = weekStart.getDate().toString().padStart(2, '0');
      const month = weekStart.toLocaleDateString('en-US', { month: 'short' });
      label = `${day}-${month}`;
    } else {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }

    if (!groups[key]) groups[key] = { avgSession: [], label };
    groups[key].avgSession.push(d.AvgSessionMinutes || 0);
  });

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, g]) => ({
      date: g.label,
      avgSession: Math.round((g.avgSession.reduce((a, b) => a + b, 0) / g.avgSession.length) * 10) / 10,
    }));
}

// Aggregate crash data by time scale
function aggregateCrashByTimeScale(crashDaily: any[], scale: TimeScale, inRange?: (d?: string | null) => boolean) {
  // Filter from Dec 2025 AND exclude today (incomplete data)
  let validData = filterFromDec2025(crashDaily, 'Date');
  const todayStr = new Date().toISOString().split('T')[0];
  validData = validData.filter(d => (d.Date || '').split('T')[0] < todayStr);
  if (inRange) validData = validData.filter(d => inRange(d.Date));
  if (validData.length === 0) return [];

  if (scale === 'daily') {
    return [...validData].reverse().map(d => ({
      date: formatDateDDMMM(d.Date || ''),
      CrashRate: d.CrashRate || 0,
    }));
  }

  const groups: Record<string, { rates: number[]; label: string }> = {};

  validData.forEach(d => {
    if (!d.Date) return;
    const date = parseLocalDate(d.Date);
    let key: string, label: string;

    if (scale === 'weekly') {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      key = weekStart.toISOString().split('T')[0];
      const day = weekStart.getDate().toString().padStart(2, '0');
      const month = weekStart.toLocaleDateString('en-US', { month: 'short' });
      label = `${day}-${month}`;
    } else {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }

    if (!groups[key]) groups[key] = { rates: [], label };
    groups[key].rates.push(d.CrashRate || 0);
  });

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, g]) => ({
      date: g.label,
      CrashRate: Math.round((g.rates.reduce((a, b) => a + b, 0) / g.rates.length) * 100) / 100,
    }));
}

function formatTimeAgo(hours: number | null): string {
  if (hours === null || hours === undefined) return 'Unknown';
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }
  return `${hours.toFixed(1)} hour${hours !== 1 ? 's' : ''} ago`;
}

function getNextUpdateTime(lastFetchHours: number | null): string {
  if (lastFetchHours === null || lastFetchHours === undefined) return 'Unknown';
  const hoursUntilNext = Math.max(0, 24 - lastFetchHours);
  if (hoursUntilNext < 1) {
    const minutes = Math.round(hoursUntilNext * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  return `${hoursUntilNext.toFixed(1)} hour${hoursUntilNext !== 1 ? 's' : ''}`;
}

// Aggregate monthly data for line graphs: returns [{date, MAU, Sessions, Hours}, ...]
// chronologically ordered (oldest first) so the chart renders left-to-right by time.
function aggregateMonthlyMetrics(monthlyStats: any[], inRange?: (d?: string | null) => boolean) {
  const dec2025 = parseLocalDate('2025-12-01');
  const filteredStats = monthlyStats.filter(m => {
    if (!m || !m.MonthStartDate) return false;
    if (parseLocalDate(String(m.MonthStartDate)) < dec2025) return false;
    // A month is in range when its start date is. Monthly datasets carry full
    // history (Mar-25 ->), so these are range-aware today with no backend work.
    return inRange ? inRange(String(m.MonthStartDate)) : true;
  });

  if (filteredStats.length === 0) return [];

  // Sort chronologically (oldest first) rather than trusting backend order, so
  // the left-to-right chart and the "Now" marker always point at the right month.
  return [...filteredStats]
    .sort((a, b) => parseLocalDate(String(a.MonthStartDate)).getTime() - parseLocalDate(String(b.MonthStartDate)).getTime())
    .map(m => ({
    date: m.Month || '',
    MAU: m.MAU || 0,
    Sessions: m.TotalSessions || 0,
    Hours: Math.round((m.TotalHours || 0) * 10) / 10,
  }));
}

// ============================================
// SIMPLE COMPONENTS
// ============================================

function ThemedCard({ children, isDark, className = '' }: { children: React.ReactNode; isDark: boolean; className?: string }) {
  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700/50' : 'bg-white border-gray-300 shadow-sm'} ${className}`}>
      {children}
    </div>
  );
}

// DOMO dataset name shown beside a section heading (the underlying source of the data)
function DatasetTag({ name, isDark }: { name: string; isDark: boolean }) {
  return (
    <span
      title={`Source: ${name}`}
      className={`ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-mono align-middle ${
        isDark ? 'bg-gray-700 text-gray-300 border border-gray-600' : 'bg-gray-100 text-gray-600 border border-gray-200'
      }`}
    >
      {name}
    </span>
  );
}

// DOMO dataset full names (so headings can show provenance at a glance)
const DS_DAILY_GAMEPLAY = 'INT+PROD| Daily Gameplay Dataset - MVP Partners';
const DS_MONTHLY_GAMEPLAY = 'INT+PROD| Monthly Gameplay Dataset - MVP Partners';
const DS_CRASH = 'PROD | Crash Reporting Dataset';
const DS_MONTHLY_RETENTION = 'PROD|Monthly Gameplay Retention - Concept (Pre-Agg)';
const DS_MONTHLY_PSPLUS_GAMEPLAY = 'PROD|Monthly PS Plus Gameplay by Concept (Pre-Agg)';
const DS_LIFETIME_GAMEPLAY = 'INT+PROD| Lifetime Gameplay Dataset - MVP Partners';

// The PS Plus Off/On (session-type) breakdown section is hidden for now — the
// data is session type, not membership, and the split was more confusing than
// useful. Flip to true to bring the section back; the backend still serves
// psPlusBreakdown so no resync/refetch is needed to re-enable.
const SHOW_PS_PLUS_BREAKDOWN = false;
// The old unfiltered DAU card (Concept+Platform rows, overcounts ~4-8x). Hidden, not
// removed: flip to true to bring it back.
const SHOW_UNFILTERED_DAU = false;
// Dashed top-3-country lines on the graphs. Hidden, not removed: flip to true to
// bring them back.
const SHOW_TOP_COUNTRY_LINES = false;

function StatCard({ title, avgValue, avgLabel, highValue, highLabel, highDate, lowValue, lowLabel, lowDate, color, isDark, datasetName }: {
  title: string; avgValue: number; avgLabel: string; highValue: number; highLabel: string; highDate: string;
  lowValue: number; lowLabel: string; lowDate: string; color: 'blue' | 'green' | 'red'; isDark: boolean; datasetName?: string;
}) {
  const borderColor = color === 'blue' ? 'border-l-blue-500' : color === 'red' ? 'border-l-red-500' : 'border-l-green-500';
  const textColor = color === 'blue' ? 'text-blue-500' : color === 'red' ? 'text-red-500' : 'text-green-500';
  // Reddish tint for the "old/unfiltered" DAU card so it reads as a caveat at a
  // glance. `!` overrides ThemedCard's default bg/border to dodge Tailwind
  // class-ordering ambiguity between two bg-* utilities.
  const tint = color === 'red'
    ? (isDark ? '!bg-red-900/20 !border-red-800/50' : '!bg-red-50 !border-red-200')
    : '';
  return (
    <ThemedCard isDark={isDark} className={`border-l-4 ${borderColor} ${tint}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
          <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            {title}
            {datasetName && <DatasetTag name={datasetName} isDark={isDark} />}
          </h3>
          <CardCountryStacked />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${textColor}`}>{avgValue.toLocaleString()}</div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{avgLabel}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <ArrowUp className="h-4 w-4 text-green-500" />
              <span className={`text-xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{highValue.toLocaleString()}</span>
            </div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{highLabel}</div>
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{highDate}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <ArrowDown className="h-4 w-4 text-red-500" />
              <span className={`text-xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>{lowValue.toLocaleString()}</span>
            </div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{lowLabel}</div>
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{lowDate}</div>
          </div>
        </div>
      </div>
    </ThemedCard>
  );
}

function CustomPieTooltip({ active, payload, isDark }: { active?: boolean; payload?: any[]; isDark: boolean }) {
  if (!active || !payload || !payload[0]) return null;
  const data = payload[0].payload;
  const regionName = REGION_NAMES[data.name] || data.name;
  const total = data.total || 1;
  return (
    <div className={`px-3 py-2 rounded-lg shadow-lg border ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
      <div className="font-semibold text-sm">{data.name} ({regionName})</div>
      <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Users: {data.value?.toLocaleString() || 0}</div>
      <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{((data.value / total) * 100).toFixed(1)}% of total</div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

const TOP_COLORS = ['#f97316', '#ec4899', '#a3e635'];
const ROLLING_WINDOW = 10;

/**
 * Every series the Sony/DOMO cards render, for ONE country selection (the tab's
 * floating filter, or a card's own). Cached per selection by the dashboard.
 */
function buildPs5View({ rawData, selection, inRange, isAllTime, rangeStart, rangeEnd,
  dauTimeScale, sessionTimeScale, crashTimeScale, psPlusMetric }: {
  rawData: PS5AnalyticsData | null; selection: string[];
  inRange: (d?: string | null) => boolean; isAllTime: boolean;
  rangeStart: string | null | undefined; rangeEnd: string | null | undefined;
  dauTimeScale: TimeScale; sessionTimeScale: TimeScale; crashTimeScale: TimeScale;
  psPlusMetric: 'mau' | 'sessions' | 'hours';
}) {
  const byCountry = (rawData as any)?.byCountry as DomoByCountry | undefined;
  const data = rawData && selection.length && byCountry ? filterDomoData(rawData, selection) : rawData;
  // Extract data with defaults - BEFORE any useMemo
  const summary = data?.summary || { totalUsers: 0, latestDAU: 0, currentMAU: 0, totalSessions: 0, totalHours: 0, avgSessionMinutes: 0, totalCountries: 0, overallCrashRate: 0, dataDate: '' };
  const dailyStats = data?.dailyStats || [];
  // Old, unfiltered (Concept+Platform only) daily DAU — overcounts ~4-8x.
  const dailyStatsUnfiltered = data?.dailyStatsUnfiltered || [];
  const monthlyStats = data?.monthlyStats || [];
  const geoBreakdown = data?.geoBreakdown || [];
  const crashStats = data?.crashStats || { daily: [], byVersion: [], byRegion: [] };
  const retentionData = data?.retentionData || [];
  const psPlusBreakdown = data?.psPlusBreakdown || [];

  // Monthly rows are keyed by the month's START date, so testing that key with the
  // plain day-level inRange() would drop any month whose 1st falls outside the
  // window -- e.g. a 05-Aug..12-Aug range would exclude August entirely and blank
  // every monthly card. A month counts when its INTERVAL overlaps the range.
  const monthInRange = (monthStart?: string | null) => {
    if (!monthStart) return false;
    if (isAllTime || (!rangeStart && !rangeEnd)) return true;
    const ms = String(monthStart).slice(0, 10);
    const d = parseLocalDate(ms);
    if (isNaN(d.getTime())) return false;
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const me = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
    if (rangeEnd && ms > rangeEnd) return false;      // month starts after range
    if (rangeStart && me < rangeStart) return false;  // month ended before range
    return true;
  };

  // PS Plus chart data: chronological (backend returns newest-first), from Dec 2025,
  // with the selected metric mapped onto Off Plus / On (PS Plus Total) / Streaming.
  const psPlusChartData = (() => {
    const metric = psPlusMetric;
    return [...psPlusBreakdown]
      .filter(m => (m.MonthStartDate || '') >= '2025-12-01')
      .filter(m => monthInRange(m.MonthStartDate || null))
      .sort((a, b) => (a.MonthStartDate || '').localeCompare(b.MonthStartDate || ''))
      .map(m => ({
        month: m.Month,
        offPlus: m.offPlus?.[metric] || 0,
        total: m.total?.[metric] || 0,
        streaming: m.streaming?.[metric] || 0,
      }));
  })();

  // ALL useMemo hooks must be called unconditionally - BEFORE any conditional returns
  const dauStats = (() => calculateDAUStats(dailyStats, inRange))();
  const dauStatsUnfiltered = (() => calculateDAUStats(dailyStatsUnfiltered, inRange))();
  // Top 3 selection (in the range, among the selection) -> one line each on every
  // trend. Hidden when only one country is selected (it would equal the total).
  const topCodes = (() => (!SHOW_TOP_COUNTRY_LINES || selection.length === 1 ? [] : topDomoCountries(byCountry, selection, ms => monthInRange(ms))))();
  const topNames = (() => topCodes.map(c => byCountry?.countries[c] || c))();
  const topData = (() => (rawData ? topCodes.map(c => filterDomoData(rawData, [c])) : []))();
  const dauChartData = (() => addCountryLines(
    aggregateByTimeScale(dailyStats, dauTimeScale, inRange),
    topData.map(d => aggregateByTimeScale(d.dailyStats || [], dauTimeScale, inRange)), 'DAU',
  ))();
  const sessionChartData = (() => addCountryLines(
    aggregateSessionByTimeScale(dailyStats, sessionTimeScale, inRange),
    topData.map(d => aggregateSessionByTimeScale(d.dailyStats || [], sessionTimeScale, inRange)), 'avgSession',
  ))();
  const monthlyChartData = (() => aggregateMonthlyMetrics(monthlyStats, monthInRange))();
  const monthlyTopData = (() => topData.map(d => aggregateMonthlyMetrics(d.monthlyStats || [], monthInRange)))();
  const monthlyByKey = (() => ({
    MAU: addCountryLines(monthlyChartData, monthlyTopData, 'MAU'),
    Sessions: addCountryLines(monthlyChartData, monthlyTopData, 'Sessions'),
    Hours: addCountryLines(monthlyChartData, monthlyTopData, 'Hours'),
  }))();
  const crashChartData = (() => addCountryLines(
    aggregateCrashByTimeScale(crashStats.daily || [], crashTimeScale, inRange),
    topData.map(d => aggregateCrashByTimeScale(d.crashStats?.daily || [], crashTimeScale, inRange)), 'CrashRate',
  ))();
  // One dashed line per top country; `valueFmt` labels the tooltip.
  const countryLines = () => topNames.map((name, i) => (
    <Line key={`top${i}`} type="monotone" dataKey={`top${i}`} name={name} stroke={TOP_COLORS[i]}
      strokeWidth={1.5} strokeDasharray="5 3" dot={false} connectNulls />
  ));
  const countryLegend = topNames.length > 0 ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null;

  // ---- Range-aware summary metrics -------------------------------------------
  // Recomputed from the per-day rows already in the payload, so the stat cards
  // track the filter instead of always showing all-time backend totals.
  const rangedSummary = (() => {
    const days = filterValidData(dailyStats).filter(d => inRange(d.Date));
    const hours = days.reduce((s, d) => s + (d.TotalHours || 0), 0);
    const sessions = days.reduce((s, d) => s + (d.TotalSessions || 0), 0);

    const crashDays = (crashStats.daily || []).filter((c: any) => inRange(c?.Date));
    const crashes = crashDays.reduce((s: number, c: any) => s + (c?.TotalCrashes || 0), 0);
    const crashSessions = crashDays.reduce((s: number, c: any) => s + (c?.TotalSessions || 0), 0);

    // Monthly rows inside the range, newest last.
    const months = [...retentionData]
      .filter(r => monthInRange(r?.monthStart))
      .sort((a, b) => String(a.monthStart).localeCompare(String(b.monthStart)));
    const lastMonth = months.length ? months[months.length - 1] : null;

    return {
      hasDaily: days.length > 0,
      hasMonthly: months.length > 0,
      dayCount: days.length,
      totalHours: hours,
      totalSessions: sessions,
      crashRate: crashSessions > 0 ? (crashes / crashSessions) * 100 : null,
      engaged: lastMonth ? (lastMonth.active || 0) : null,
      engagedMonth: lastMonth ? (lastMonth.month || '') : '',
      // Cumulative-as-of-range-end (the card means "cumulative unique players").
      cumulativeUsers: lastMonth ? (lastMonth.acquiredLTD || 0) : null,
    };
  })();

  // ---- Daily active accounts by country / region (Title-Level dataset) -------
  // Columnar wire format (see _fetch_geo_daily): selection indexed once, then
  // [countryIdx, accounts, sessions] triples per day (60 KB, not 600 KB).
  //
  // `accounts` on a SINGLE day is unique active accounts for that day -- a real
  // DAU. It is only SUMMING across days that would produce player-days, so
  // everything here works in daily values and monthly AVERAGES of them, never
  // cross-day sums.
  const geoDaily = (data as any)?.geoDaily as
    | { countries?: Array<[string, string]>; days?: Record<string, Array<[number, number, number]>> }
    | undefined;


  const geoSeries = (() => {
    const names = geoDaily?.countries || [];
    const rawDays = geoDaily?.days || {};
    const dates = Object.keys(rawDays).filter(d => inRange(d)).sort();

    // country -> {date: dau}, region -> {date: dau}
    const cByDate: Record<string, Record<string, number>> = {};
    const rByDate: Record<string, Record<string, number>> = {};
    const cTotal: Record<string, number> = {};
    for (const d of dates) {
      for (const [ci, accounts] of rawDays[d]) {
        const entry = names[ci];
        if (!entry) continue;
        const [country, region] = entry;
        const rg = REGION_DISPLAY[region] || region || 'Other';
        (cByDate[country] ||= {})[d] = ((cByDate[country] || {})[d] || 0) + (accounts || 0);
        (rByDate[rg] ||= {})[d] = ((rByDate[rg] || {})[d] || 0) + (accounts || 0);
        cTotal[country] = (cTotal[country] || 0) + (accounts || 0);
      }
    }

    const topCountries = Object.keys(cTotal).sort((a, b) => cTotal[b] - cTotal[a]).slice(0, 15);
    const regions = Object.keys(rByDate).sort(
      (a, b) => Object.values(rByDate[b]).reduce((x, y) => x + y, 0)
              - Object.values(rByDate[a]).reduce((x, y) => x + y, 0));

    // N-day trailing mean, so a single spiky day doesn't dominate the shape.
    const rolling = (keys: string[], src: Record<string, Record<string, number>>) =>
      dates.map((d, i) => {
        const row: Record<string, number | string> = { date: d };
        const from = Math.max(0, i - ROLLING_WINDOW + 1);
        for (const k of keys) {
          let sum = 0;
          for (let j = from; j <= i; j++) sum += src[k]?.[dates[j]] || 0;
          row[k] = Math.round((sum / (i - from + 1)) * 10) / 10;
        }
        return row;
      });

    // Monthly AVERAGE of daily values (never a cross-day sum).
    const monthAvg = (keys: string[], src: Record<string, Record<string, number>>) => {
      const months = Array.from(new Set(dates.map(d => d.slice(0, 7)))).sort();
      const table = keys.map(k => {
        const cells: Record<string, number> = {};
        for (const m of months) {
          const vals = dates.filter(d => d.startsWith(m)).map(d => src[k]?.[d] || 0);
          cells[m] = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
        }
        return { key: k, cells };
      });
      return { months, table };
    };

    return {
      dates,
      topCountries,
      regions,
      countryChart: rolling(topCountries, cByDate),
      regionChart: rolling(regions, rByDate),
      countryTable: monthAvg(topCountries, cByDate),
      regionTable: monthAvg(regions, rByDate),
    };
  })();

  // Daily datasets only go back ~91 days (Sony retention). A range starting before
  // that window is legitimately empty, not a bug -- say so instead of showing 0s.
  const dailyWindowStart = (() => {
    const ds = filterValidData(dailyStats).map(d => (d.Date || '').slice(0, 10)).filter(Boolean).sort();
    return ds.length ? ds[0] : null;
  })();

  // Sum a per-month series (Part C ships downloadsByMonth / installsByMonth) over
  // the selected range. Returns null when the series isn't present, so the card
  // can fall back to the backend's all-time scalar rather than showing a wrong 0.
  const sumByMonth = (series?: Array<{ monthStart?: string; value?: number }>) => {
    if (!series || !series.length) return null;
    return series
      .filter(m => monthInRange(m?.monthStart || null))
      .reduce((s, m) => s + (m?.value || 0), 0);
  };

  // Quick-stat cards. When no range is active every card shows the backend's
  // all-time figure (unchanged behaviour); with a range they recompute from the
  // date-keyed data. `note` marks cards that cannot honour the filter.
  const quickStats = ((() => {
    const dl = sumByMonth((data as any)?.downloadsByMonth);
    const inst = sumByMonth((data as any)?.installsByMonth);
    const fmt = (n: number) => Math.round(n).toLocaleString();

    return [
      {
        label: 'Total Downloads',
        value: (!isAllTime && dl !== null ? dl : (summary.totalDownloads || 0)).toLocaleString(),
        subtitle: !isAllTime && dl === null
          ? 'Game Hub "Download" clicks — all time (no monthly series yet)'
          : 'Game Hub "Download" clicks — distinct accounts/month (not PS Store sales)',
      },
      {
        label: 'Total Installs',
        value: (!isAllTime && inst !== null ? inst : (summary.totalInstalls || 0)).toLocaleString(),
        subtitle: !isAllTime && inst === null
          ? 'First-time + reinstalls — all time (no monthly series yet)'
          : 'Total installs (first-time + reinstalls)',
      },
      {
        label: isAllTime ? 'All Time PS5 Users' : 'PS5 Users (cumulative)',
        value: (!isAllTime && rangedSummary.cumulativeUsers !== null
          ? rangedSummary.cumulativeUsers
          : (summary.allTimeUsers || summary.totalUniqueUsers || summary.totalUsers || 0)).toLocaleString(),
        subtitle: isAllTime
          ? 'Cumulative unique PS5 players'
          : `Cumulative as of ${rangedSummary.engagedMonth || 'range end'}`,
      },
      {
        label: 'Total Session Hours',
        value: !isAllTime
          ? fmt(rangedSummary.totalHours)
          : fmt(summary.totalPlaytimeHours ?? summary.totalHours ?? 0),
        subtitle: !isAllTime
          ? `${rangedSummary.dayCount} day(s) in range`
          : 'Cumulative play time across all PS5 sessions (since Dec 2025)',
      },
      {
        label: 'Currently Active',
        value: (!isAllTime && rangedSummary.engaged !== null
          ? rangedSummary.engaged
          : (summary.currentEngaged || 0)).toLocaleString(),
        subtitle: !isAllTime
          ? `${rangedSummary.engagedMonth || 'no month in range'} (new + retained + reclaimed)`
          : `${summary.currentEngagedMonth || 'Last month'} (new + retained + reclaimed)`,
      },
      (() => {
        const rate = !isAllTime ? rangedSummary.crashRate : (summary.currentCrashRate || 0);
        return {
          label: 'Crash Rate',
          value: rate === null ? '—' : `${rate.toFixed(2)}%`,
          isRed: (rate || 0) > 1,
          subtitle: !isAllTime
            ? (rate === null ? 'no crash data in range' : `${rangedSummary.dayCount} day(s) in range`)
            : (summary.currentCrashDate ? `as of ${summary.currentCrashDate.split('T')[0]}` : 'Latest day'),
        };
      })(),
    ];
  }) as () => Array<{ label: string; value: string; subtitle: string; isRed?: boolean }>)();
  
  const totalUsers = (() => geoBreakdown.reduce((sum, g) => sum + (g?.Users || 0), 0))();
  const pieData = (() => {
    const rd = geoBreakdown.reduce((acc, g) => {
      if (!g) return acc;
      const r = g.Region || 'Other';
      if (!acc[r]) acc[r] = { name: r, value: 0, total: totalUsers };
      acc[r].value += g.Users || 0;
      return acc;
    }, {} as Record<string, { name: string; value: number; total: number }>);
    return Object.values(rd).sort((a, b) => b.value - a.value);
  })();

  return { byCountry, countryLegend, countryLines, crashChartData, crashStats, dailyStats, dailyStatsUnfiltered, dailyWindowStart, data, dauChartData, dauStats, dauStatsUnfiltered, geoBreakdown, geoDaily, geoSeries, monthInRange, monthlyByKey, monthlyChartData, monthlyStats, monthlyTopData, pieData, psPlusBreakdown, psPlusChartData, quickStats, rangedSummary, retentionData, selection, sessionChartData, sumByMonth, summary, topCodes, topData, topNames, totalUsers };
}

type Ps5View = ReturnType<typeof buildPs5View>;

export function PS5Dashboard() {
  const { isDarkMode } = useTheme();
  const { inRange, isAllTime, start: rangeStart, end: rangeEnd } = useDateRange();
  const { data: rawData, cacheStatus, isLoading, isRefreshing, error, refresh, exportPDF, isConfigured } = usePS5Analytics();

  // ---- Country filter (this tab only; [] = All) ----
  // Every series is rebuilt from the per-country rows the backend ships, so all
  // cards below follow the selection. Top-3 country lines are added to each trend.
  const [countries, setCountries] = useState<string[]>([]);
  const byCountry = (rawData as any)?.byCountry as DomoByCountry | undefined;
  const cardOverrides = useCardOverrides();
  const countryOpts = useMemo(() => domoCountryOptions(byCountry), [byCountry]);
  
  // ALL useState hooks must be called unconditionally
  const [isExporting, setIsExporting] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [dauTimeScale, setDauTimeScale] = useState<TimeScale>('daily');
  const [sessionTimeScale, setSessionTimeScale] = useState<TimeScale>('daily');
  const [crashTimeScale, setCrashTimeScale] = useState<TimeScale>('daily');
  // PS Plus session-type view: which metric to chart and which segment(s) to show.
  const [psPlusMetric, setPsPlusMetric] = useState<'mau' | 'sessions' | 'hours'>('mau');
  const [psPlusSegment, setPsPlusSegment] = useState<'all' | 'offPlus' | 'total' | 'streaming'>('all');

  const buildView = useCallback((selection: string[]) => buildPs5View({
    rawData, selection, inRange, isAllTime, rangeStart, rangeEnd,
    dauTimeScale, sessionTimeScale, crashTimeScale, psPlusMetric,
  }), [rawData, inRange, isAllTime, rangeStart, rangeEnd, dauTimeScale, sessionTimeScale, crashTimeScale, psPlusMetric]);
  const viewFor = useViewCache(buildView);
  const { dailyWindowStart, data, geoSeries, monthlyChartData, psPlusChartData, quickStats } = viewFor(countries);
  /** Renders a card from the view for ITS countries (own override, else the tab's). */
  const CC = (id: string, render: (v: Ps5View) => ReactNode) => (
    <CountryCard id={id}>{selection => render(viewFor(selection))}</CountryCard>
  );

  // Constants for rendering
  const gridColor = isDarkMode ? '#374151' : '#e5e7eb';
  const textColor = isDarkMode ? '#9ca3af' : '#6b7280';
  const tooltipStyle = { backgroundColor: isDarkMode ? '#1f2937' : '#ffffff', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`, borderRadius: '8px' };
  const REGION_COLORS: Record<string, string> = { 'SIEA': '#3b82f6', 'SIEE': '#10b981', 'SIEAsia': '#f59e0b', 'SIEJ': '#ef4444' };

  // Monthly trend cards (MAU / Sessions / Session Hours), all from INT+PROD Monthly
  // Gameplay. Rendered individually so they can be ordered around the other charts.
  type MonthlyTrendCfg = {
    key: 'MAU' | 'Sessions' | 'Hours'; title: string; color: string;
    fmt: (v: number) => string; yFmt: (v: number) => string;
    athLabel: (v: number) => string; nowLabel: (v: number) => string;
  };
  const monthlyTrendCfgs: Record<'mau' | 'sessions' | 'hours', MonthlyTrendCfg> = {
    mau: { key: 'MAU', title: 'Monthly Active Users (MAU) Trend', color: '#10b981',
      fmt: (v) => v.toLocaleString(), yFmt: formatAxisCount,
      athLabel: (v) => `ATH ${v.toLocaleString()}`, nowLabel: (v) => `Now ${v.toLocaleString()}` },
    sessions: { key: 'Sessions', title: 'Monthly Sessions Trend', color: '#f59e0b',
      fmt: (v) => v.toLocaleString(), yFmt: formatAxisCount,
      athLabel: (v) => `ATH ${v.toLocaleString()}`, nowLabel: (v) => `Now ${v.toLocaleString()}` },
    hours: { key: 'Hours', title: 'Monthly Session Hours Trend', color: '#8b5cf6',
      fmt: (v) => `${v.toLocaleString()} h`, yFmt: formatAxisCount,
      athLabel: (v) => `ATH ${Math.round(v).toLocaleString()}h`, nowLabel: (v) => `Now ${Math.round(v).toLocaleString()}h` },
  };
  const renderMonthlyTrend = (cfg: MonthlyTrendCfg, v: Ps5View) => (
    <ThemedCard isDark={isDarkMode} key={cfg.key}>
      <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          {cfg.title}
          <DatasetTag name={DS_MONTHLY_GAMEPLAY} isDark={isDarkMode} />
        </h3>
        <CardCountryStacked />
      </div>
      <div className="p-4 h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={v.monthlyByKey[cfg.key]} margin={MARKER_CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{payload.value}</text>)} stroke={gridColor} height={60} />
            <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={cfg.yFmt} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [cfg.fmt(v), name]} />
            {v.countryLegend}
            {monthlyChartData.length > 0 && (() => {
              const series = monthlyChartData.map(d => (d as any)[cfg.key] as number);
              const ath = Math.max(...series);
              const cur = series[series.length - 1];
              return (<>
                {ath > 0 && <ReferenceLine y={ath} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
                  label={markerLabel(cfg.athLabel(ath), '#ef4444', 'above')} />}
                {cur > 0 && cur !== ath && <ReferenceLine y={cur} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                  label={markerLabel(cfg.nowLabel(cur), '#22c55e', 'below')} />}
              </>);
            })()}
            <Line type="monotone" dataKey={cfg.key} name={v.selection.length ? 'Selected countries' : cfg.title.replace(' Trend', '')}
              stroke={cfg.color} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
            {v.countryLines()}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ThemedCard>
  );

  const handleExportPDF = async () => {
    setIsExporting(true);
    try { await exportPDF(countries); } catch (err) { console.error('Export failed:', err); }
    finally { setIsExporting(false); }
  };


  // NOW we can have conditional returns - AFTER all hooks
  
  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className={`h-8 w-8 animate-spin mx-auto mb-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading Sony/DOMO Analytics...</p>
        </div>
      </div>
    );
  }

  // Not configured state
  if (!isConfigured) {
    return (
      <div className="p-6">
        <ThemedCard isDark={isDarkMode}>
          <div className="p-8 text-center">
            <AlertTriangle className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Sony/DOMO Analytics Not Configured</h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} max-w-md mx-auto`}>
              Sony Domo API credentials are not configured. Please add DOMO_CLIENT_ID and DOMO_CLIENT_SECRET to your environment variables.
            </p>
          </div>
        </ThemedCard>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="p-6">
        <ThemedCard isDark={isDarkMode}>
          <div className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Failed to Load Data</h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>{error}</p>
            <button onClick={() => refresh()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Retry</button>
          </div>
        </ThemedCard>
      </div>
    );
  }

  // Main dashboard render
  return (
    <CountryScope value={{
      tabSelection: countries, options: countryOpts, isDark: isDarkMode, countLabel: 'lifetime PS5 players',
      disabled: !byCountry, disabledReason: 'Country data appears after the next Sony/DOMO refresh',
      ...cardOverrides,
    }}>
    <>
      {/* Floating date-range card, matching the Analytics tab: pinned top-left
          under the sticky header so it stays reachable while scrolling. z-40 sits
          below the z-50 header/modals. Hidden below xl where it would overlap the
          charts (the inline copy in the header covers those widths). */}
      <div className="hidden xl:block fixed left-4 top-[7.5rem] z-40">
        <div
          className={`w-48 rounded-xl border shadow-lg backdrop-blur-sm p-3 ${
            isDarkMode ? 'bg-gray-900/90 border-gray-700' : 'bg-white/90 border-gray-200'
          }`}
        >
          <div
            className={`flex items-center gap-1.5 mb-2.5 text-[11px] font-semibold uppercase tracking-wider ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Date Range
          </div>
          <DateRangeFilter orientation="vertical" />
          <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <Globe className="h-3.5 w-3.5" />
              Country/Region
            </div>
            <CountryMultiSelect options={countryOpts} selected={countries} onChange={setCountries} isDark={isDarkMode}
              compact align="left" countLabel="lifetime PS5 players" disabled={!byCountry}
              disabledReason="Country data appears after the next Sony/DOMO refresh" />
            {byCountry && (
              <div className="mt-3">
                <CountryChips options={countryOpts} selected={countries} onChange={setCountries} isDark={isDarkMode} vertical max={8} />
              </div>
            )}
          </div>
        </div>
      </div>

    <div className="p-6 space-y-6 xl:pl-56">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg"><Gamepad2 className="h-6 w-6 text-white" /></div>
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Sony / DOMO Analytics</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Data from Sony PlayStation Partners API</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Inline filter for < xl only; at xl+ the floating card above takes over. */}
          <div className="xl:hidden">
            <DateRangeFilter />
          </div>
          {/* Below xl the floating card is hidden: same tab filter inline. */}
          <div className="xl:hidden">
            <CountryMultiSelect options={countryOpts} selected={countries} onChange={setCountries} isDark={isDarkMode}
              countLabel="lifetime PS5 players" disabled={!byCountry}
              disabledReason="Country data appears after the next Sony/DOMO refresh" />
          </div>
          {cacheStatus && (
            <div className={`flex flex-col px-3 py-1.5 rounded-lg text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              <div><span className="font-medium">Last Update:</span> {formatTimeAgo(cacheStatus.cacheAgeHours)}</div>
              <div><span className="font-medium">Next Update:</span> {getNextUpdateTime(cacheStatus.cacheAgeHours)}</div>
            </div>
          )}
          <button onClick={() => refresh()} disabled={isRefreshing}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-50`}>
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />Refresh
          </button>
          <button onClick={handleExportPDF} disabled={isExporting}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50">
            <Download className={`h-4 w-4 ${isExporting ? 'animate-pulse' : ''}`} />Export PDF
          </button>
        </div>
      </div>

      {/* DAU Statistics */}
      {CC('dau-stats', v => (<>
      <div className="grid grid-cols-1 gap-4">
        <StatCard title="Daily Active Users (DAU) Statistics" avgValue={v.dauStats.avg} avgLabel="Average DAU"
          highValue={v.dauStats.highest} highLabel="Highest DAU" highDate={v.dauStats.highestDate}
          lowValue={v.dauStats.lowest} lowLabel="Lowest DAU" lowDate={v.dauStats.lowestDate} color="blue" isDark={isDarkMode}
          datasetName={DS_DAILY_GAMEPLAY} />
        {SHOW_UNFILTERED_DAU && (
          <StatCard title="DAU — Unfiltered / Old (Concept+Platform, overcounts ~4-8×)" avgValue={v.dauStatsUnfiltered.avg} avgLabel="Average DAU"
            highValue={v.dauStatsUnfiltered.highest} highLabel="Highest DAU" highDate={v.dauStatsUnfiltered.highestDate}
            lowValue={v.dauStatsUnfiltered.lowest} lowLabel="Lowest DAU" lowDate={v.dauStatsUnfiltered.lowestDate} color="red" isDark={isDarkMode}
            datasetName={DS_DAILY_GAMEPLAY} />
        )}
      </div>
      </>))}

      {/* Daily-window notice: Sony keeps ~91 days of DAILY data. A range starting
          before that is legitimately empty for the daily cards, so say so rather
          than rendering zeros that look like an outage. */}
      {!isAllTime && dailyWindowStart && rangeStart && rangeStart < dailyWindowStart && (
        <ThemedCard isDark={isDarkMode}>
          <div className={`p-3 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
            Daily data (DAU, sessions, crashes) only exists from <b>{dailyWindowStart}</b> —
            Sony retains ~91 days. Monthly cards below still cover the full selected range.
          </div>
        </ThemedCard>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {quickStats.map((s, i) => (
          <ThemedCard key={i} isDark={isDarkMode}>
            <div className="p-3 text-center">
              <div className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
              <div className={`text-xl font-bold ${s.isRed ? 'text-red-500' : isDarkMode ? 'text-white' : 'text-gray-900'}`}>{s.value}</div>
              {s.subtitle && <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} max-w-[160px] mx-auto`}>{s.subtitle}</div>}
            </div>
          </ThemedCard>
        ))}
      </div>

      {/* PS Plus — Off / On (Session Type) Breakdown (hidden via flag) */}
      {SHOW_PS_PLUS_BREAKDOWN && psPlusChartData.length > 0 && (() => {
        const metricLabel = psPlusMetric === 'mau' ? 'Accounts (MAU)' : psPlusMetric === 'sessions' ? 'Sessions' : 'Session Hours';
        const fmt = (v: number) => psPlusMetric === 'hours' ? `${Math.round(v).toLocaleString()}` : v.toLocaleString();
        const latest = psPlusChartData[psPlusChartData.length - 1];
        const showOff = psPlusSegment === 'all' || psPlusSegment === 'offPlus';
        const showTotal = psPlusSegment === 'all' || psPlusSegment === 'total';
        const showStreaming = psPlusSegment === 'streaming';
        const metricOpts: { v: typeof psPlusMetric; label: string }[] = [
          { v: 'mau', label: 'MAU' }, { v: 'sessions', label: 'Sessions' }, { v: 'hours', label: 'Hours' },
        ];
        const segOpts: { v: typeof psPlusSegment; label: string }[] = [
          { v: 'all', label: 'Off vs On' }, { v: 'offPlus', label: 'Off Plus' },
          { v: 'total', label: 'On (PS+ Total)' }, { v: 'streaming', label: 'Streaming' },
        ];
        return (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between flex-wrap gap-2">
              <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                PS Plus — Off / On Breakdown ({metricLabel}, monthly)
                <DatasetTag name={DS_MONTHLY_PSPLUS_GAMEPLAY} isDark={isDarkMode} />
              </h3>
              <div className="flex gap-2 flex-wrap">
                <div className={`flex rounded-lg p-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  {metricOpts.map(o => (
                    <button key={o.v} onClick={() => setPsPlusMetric(o.v)}
                      className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                        psPlusMetric === o.v
                          ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 shadow-sm'
                          : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                      }`}>{o.label}</button>
                  ))}
                </div>
                <div className={`flex rounded-lg p-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  {segOpts.map(o => (
                    <button key={o.v} onClick={() => setPsPlusSegment(o.v)}
                      className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                        psPlusSegment === o.v
                          ? isDarkMode ? 'bg-purple-600 text-white' : 'bg-white text-purple-600 shadow-sm'
                          : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                      }`}>{o.label}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sony caveat: these are SESSION TYPES, not membership */}
            <div className={`mx-4 mt-3 px-3 py-2 rounded-lg border text-xs flex items-start gap-2 ${isDarkMode ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Off Plus / PS Plus are session types, not membership.</strong> Sony does not report who holds a PS Plus
                subscription. "Off Plus" = plays outside the PS Plus context; "PS Plus Total/Streaming" = PS Plus cloud-streaming activity.
                This dataset only covers the subset Sony classifies into these types, so totals are lower than the canonical MAU above.
              </span>
            </div>

            {/* Latest-month Off vs On mini-cards */}
            {latest && (
              <div className="px-4 pt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className={`rounded-lg p-3 text-center ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-slate-50 border border-slate-100'}`}>
                  <div className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Off Plus · {latest.month}</div>
                  <div className="text-xl font-bold" style={{ color: '#64748b' }}>{fmt(latest.offPlus)}</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-purple-50 border border-purple-100'}`}>
                  <div className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>On (PS+ Total) · {latest.month}</div>
                  <div className="text-xl font-bold" style={{ color: '#a855f7' }}>{fmt(latest.total)}</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-cyan-50 border border-cyan-100'}`}>
                  <div className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Streaming · {latest.month}</div>
                  <div className="text-xl font-bold" style={{ color: '#06b6d4' }}>{fmt(latest.streaming)}</div>
                </div>
              </div>
            )}

            <div className="p-4 overflow-x-auto">
              <div style={{ minWidth: Math.max(400, psPlusChartData.length * 90) }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={psPlusChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} interval={0} />
                    <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={formatAxisCount} />
                    <Tooltip
                      contentStyle={{ backgroundColor: isDarkMode ? '#1f2937' : '#fff', border: `1px solid ${gridColor}`, borderRadius: 8, fontSize: 12, color: textColor }}
                      formatter={(value: number, name: string) => [fmt(value), name]}
                    />
                    {showOff && <Bar dataKey="offPlus" name="Off Plus" fill="#64748b" radius={[4, 4, 0, 0]} />}
                    {showTotal && <Bar dataKey="total" name="PS Plus Total (On)" fill="#a855f7" radius={[4, 4, 0, 0]} />}
                    {showStreaming && <Bar dataKey="streaming" name="PS Plus Streaming" fill="#06b6d4" radius={[4, 4, 0, 0]} />}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ThemedCard>
        );
      })()}

      {/* Monthly Retention Rate */}
      {CC('retention', v => (<>
      {(() => {
        const allRetention = v.retentionData.filter(r => r.monthStart >= '2025-12-01');
        // Build month-over-month pairs from the FULL series first, THEN drop the
        // out-of-range ones. Filtering before pairing would lose the first
        // in-range bar, because each rate needs its predecessor month.
        const retentionPairs = allRetention.slice(1).map((r, i) => {
          const prev = allRetention[i];
          const prevActive = prev.active;
          // Retention = Retained / previous month's active. Reclaimed players (lapsed
          // earlier, came back) are NOT counted: they did not carry over from last month.
          const rate = prevActive > 0 ? Math.round(r.retained / prevActive * 1000) / 10 : 0;
          return {
            monthStart: r.monthStart,
            period: `${prev.month} to ${r.month}`,
            rate, prevActive,
            acquired: r.acquired, retained: r.retained, reclaimed: r.reclaimed,
            lapsed: r.lapsed, active: r.active,
          };
        }).filter(p => v.monthInRange(p.monthStart));
        // Same pairing per top country, joined on the period label.
        const topRates = v.topData.map(d => {
          const all = (d.retentionData || []).filter(r => r.monthStart >= '2025-12-01');
          return new Map(all.slice(1).map((r, i) => {
            const prevActive = all[i].active;
            return [`${all[i].month} to ${r.month}`, prevActive > 0 ? Math.round(r.retained / prevActive * 1000) / 10 : 0];
          }));
        });
        retentionPairs.forEach(p => topRates.forEach((m, i) => { (p as any)[`top${i}`] = m.get(p.period); }));
        const filteredRetention = retentionPairs;
        return retentionPairs.length > 0 ? (
        <ThemedCard isDark={isDarkMode}>
          <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Monthly Retention Rate
              <DatasetTag name={DS_MONTHLY_RETENTION} isDark={isDarkMode} />
            </h3>
            <CardCountryStacked />
          </div>
          <div className="p-4 overflow-x-auto">
            <div style={{ minWidth: Math.max(400, filteredRetention.length * 120) }}>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={retentionPairs}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="period" tick={{ fontSize: 9, fill: textColor }} stroke={gridColor} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      if (!d) return null;
                      return (
                        <div className={`p-3 rounded-lg border text-xs ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}>
                          <div className="font-semibold mb-1">{label}</div>
                          <div>Retention: <span className="font-bold text-green-400">{d.rate}%</span></div>
                          <div>Retained: {d.retained.toLocaleString()} / {d.prevActive.toLocaleString()} active last month</div>
                          <hr className={`my-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                          <div>New: {d.acquired.toLocaleString()}</div>
                          <div>Retained: {d.retained.toLocaleString()}</div>
                          <div>Reclaimed: {d.reclaimed.toLocaleString()}</div>
                          <div>Lapsed: {d.lapsed.toLocaleString()}</div>
                          <div className="font-semibold mt-1">Active: {d.active.toLocaleString()}</div>
                          {v.topNames.map((n, i) => d[`top${i}`] !== undefined && (
                            <div key={n} style={{ color: TOP_COLORS[i] }}>{n}: {d[`top${i}`]}%</div>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="rate" fill="#10b981" radius={[4, 4, 0, 0]}
                    label={{ position: 'top', fill: textColor, fontSize: 11, fontWeight: 600, formatter: (v: number) => `${v}%` }}
                    name={countries.length ? 'Retention · selected' : 'Retention'}
                  />
                  {v.countryLines()}
                  {v.countryLegend}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </ThemedCard>
        ) : null;
      })()}
      </>))}

      {/* DAU Trend Chart */}
      {CC('dau-trend', v => (<>
      <ThemedCard isDark={isDarkMode}>
        <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
          <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Daily Active Users Trend (from Dec 2025)
            <DatasetTag name={DS_DAILY_GAMEPLAY} isDark={isDarkMode} />
          </h3>
          <CardCountryStacked />
          <TimeScaleToggle scale={dauTimeScale} onChange={setDauTimeScale} isDark={isDarkMode} />
        </div>
        <div className="p-4 h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={v.dauChartData} margin={MARKER_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="date" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{payload.value}</text>)} stroke={gridColor} height={60} />
              <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={formatAxisCount} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [v.toLocaleString(), name]} />
              {v.countryLegend}
              {/* ATH marker */}
              {v.dauChartData.length > 0 && (() => {
                const ath = Math.max(...v.dauChartData.map(d => d.DAU));
                const cur = v.dauChartData[v.dauChartData.length - 1].DAU;
                return (<>
                  {ath > 0 && <ReferenceLine y={ath} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
                    label={markerLabel(`ATH ${ath.toLocaleString()}`, '#ef4444', 'above')} />}
                  {cur > 0 && cur !== ath && <ReferenceLine y={cur} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                    label={markerLabel(`Now ${cur.toLocaleString()}`, '#22c55e', 'below')} />}
                </>);
              })()}
              <Line type="monotone" dataKey="DAU" name={countries.length ? 'DAU · selected' : 'DAU'} stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              {v.countryLines()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ThemedCard>
      </>))}

      {/* MAU graph by month */}
      {CC('mau', v => renderMonthlyTrend(monthlyTrendCfgs.mau, v))}

      {/* Crash Rate Chart */}
      {CC('crash', v => (<>
      <ThemedCard isDark={isDarkMode}>
        <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
          <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            <AlertTriangle className="h-4 w-4 text-red-400" />Crash Rate Trend (from Dec 2025)
            <DatasetTag name={DS_CRASH} isDark={isDarkMode} />
          </h3>
          <CardCountryStacked />
          <TimeScaleToggle scale={crashTimeScale} onChange={setCrashTimeScale} isDark={isDarkMode} />
        </div>
        <div className="p-4 h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={v.crashChartData} margin={MARKER_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="date" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{payload.value}</text>)} stroke={gridColor} height={60} />
              <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={(v) => `${v}%`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [`${v.toFixed(2)}%`, name]} />
              {v.countryLegend}
              {/* ATH marker */}
              {v.crashChartData.length > 0 && (() => {
                const ath = Math.max(...v.crashChartData.map(d => d.CrashRate));
                const cur = v.crashChartData[v.crashChartData.length - 1].CrashRate;
                return (<>
                  {ath > 0 && <ReferenceLine y={ath} stroke="#f59e0b" strokeDasharray="4 3" strokeWidth={1.5}
                    label={markerLabel(`ATH ${ath.toFixed(2)}%`, '#f59e0b', 'above')} />}
                  {cur > 0 && cur !== ath && <ReferenceLine y={cur} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                    label={markerLabel(`Now ${cur.toFixed(2)}%`, '#22c55e', 'below')} />}
                </>);
              })()}
              <Line type="monotone" dataKey="CrashRate" name={countries.length ? 'Crash rate · selected' : 'Crash Rate'} stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              {v.countryLines()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ThemedCard>
      </>))}

      {/* Average Session Time Trend */}
      {CC('session-time', v => (<>
      <ThemedCard isDark={isDarkMode}>
        <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
          <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Average Session Time Trend
            <DatasetTag name={DS_DAILY_GAMEPLAY} isDark={isDarkMode} />
          </h3>
          <CardCountryStacked />
          <TimeScaleToggle scale={sessionTimeScale} onChange={setSessionTimeScale} isDark={isDarkMode} />
        </div>
        <div className="p-4 h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={v.sessionChartData} margin={MARKER_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="date" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{payload.value}</text>)} stroke={gridColor} height={60} />
              <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={(v) => `${v}m`} domain={['auto', 'auto']} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [`${v.toFixed(1)} min`, name]} />
              {v.countryLegend}
              {/* ATH marker */}
              {v.sessionChartData.length > 0 && (() => {
                const ath = Math.max(...v.sessionChartData.map(d => d.avgSession));
                const cur = v.sessionChartData[v.sessionChartData.length - 1].avgSession;
                return (<>
                  {ath > 0 && <ReferenceLine y={ath} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
                    label={markerLabel(`ATH ${ath.toFixed(1)}m`, '#ef4444', 'above')} />}
                  {cur > 0 && cur !== ath && <ReferenceLine y={cur} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                    label={markerLabel(`Now ${cur.toFixed(1)}m`, '#22c55e', 'below')} />}
                </>);
              })()}
              <Line type="monotone" dataKey="avgSession" name={countries.length ? 'Avg session · selected' : 'Avg Session'} stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              {v.countryLines()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ThemedCard>
      </>))}

      {/* Monthly Sessions Trend */}
      {CC('sessions', v => renderMonthlyTrend(monthlyTrendCfgs.sessions, v))}

      {/* Monthly Session Hours Trend */}
      {CC('hours', v => renderMonthlyTrend(monthlyTrendCfgs.hours, v))}

      {/* Geographic Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {CC('geo-regions', v => (<>
        <ThemedCard isDark={isDarkMode}>
          <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              PS5 Players by Region
              {!isAllTime && <span className={`ml-2 text-[10px] font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(all time)</span>}
              <DatasetTag name={DS_LIFETIME_GAMEPLAY} isDark={isDarkMode} />
            </h3>
            <CardCountryStacked />
          </div>
          <div className="p-4 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={v.pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: textColor }}>
                  {v.pieData.map((entry, i) => <Cell key={`cell-${i}`} fill={REGION_COLORS[entry.name] || '#6b7280'} />)}
                </Pie>
                <Tooltip content={<CustomPieTooltip isDark={isDarkMode} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ThemedCard>
        </>))}

        {CC('geo-countries', v => (<>
        <ThemedCard isDark={isDarkMode} className="lg:col-span-2">
          <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              PS5 Players by Country
              {!isAllTime && <span className={`ml-2 text-[10px] font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(all time — lifetime dataset has no date dimension)</span>}
              <DatasetTag name={DS_LIFETIME_GAMEPLAY} isDark={isDarkMode} />
            </h3>
            <CardCountryStacked />
            <button onClick={() => setShowAllCountries(!showAllCountries)} className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {showAllCountries ? 'Show Less' : `Show All (${v.geoBreakdown.length})`}
              {showAllCountries ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
          <div className="p-4 overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                <th className="text-left py-2">Country</th><th className="text-left py-2">Region</th>
                <th className="text-right py-2">Players</th><th className="text-right py-2">%</th>
              </tr></thead>
              <tbody>
                {(showAllCountries ? v.geoBreakdown : v.geoBreakdown.slice(0, 10)).map((g, i) => (
                  <tr key={g?.Country || i} className={`border-t ${isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-700'}`}>
                    <td className="py-2">{g?.Country || ''}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        g?.Region === 'SIEA' ? 'bg-blue-500/20 text-blue-400' :
                        g?.Region === 'SIEE' ? 'bg-green-500/20 text-green-400' :
                        g?.Region === 'SIEAsia' ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400'
                      }`}>{g?.Region || ''} ({REGION_NAMES[g?.Region || ''] || ''})</span>
                    </td>
                    <td className="text-right py-2">{(g?.Users || 0).toLocaleString()}</td>
                    <td className="text-right py-2">{(g?.Percentage || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ThemedCard>
        </>))}
      </div>

      {/* ---- Daily active accounts: rolling-average lines + month-average tables ----
           Sourced from the Title-Level Gameplay dataset, the only one carrying
           Date + Country together. Daily values are true DAU (unique accounts
           that day); the tables average those dailies per month rather than
           summing across days, so nothing double-counts a returning player. */}
      {geoSeries.dates.length > 0 && (() => {
        const axisTick = { fontSize: 11, fill: textColor };
        const monthTicks = geoSeries.dates.filter((d, i) =>
          i === 0 || d.slice(0, 7) !== geoSeries.dates[i - 1].slice(0, 7));

        const LineChartCard = ({ title, rows, keys, colorFor }: {
          title: string; rows: any[]; keys: string[]; colorFor: (k: string, i: number) => string;
        }) => (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {ROLLING_WINDOW}-day rolling average
                </div>
                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {title}
                  <DatasetTag name="PROD|Daily Title-Lvl Gameplay Dataset" isDark={isDarkMode} />
                </h3>
              </div>
              <CardCountryStacked />
            </div>
            <div className="p-4 h-[420px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={{ top: 5, right: 24, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis dataKey="date" ticks={monthTicks} tick={axisTick} stroke={gridColor}
                    tickFormatter={(d: string) => monthLabel(String(d).slice(0, 7))} />
                  <YAxis tick={axisTick} stroke={gridColor} width={48} />
                  <Tooltip contentStyle={tooltipStyle}
                    formatter={(v: number, n: string) => [Number(v).toLocaleString(), n]} />
                  <Legend layout="vertical" align="right" verticalAlign="middle"
                    wrapperStyle={{ fontSize: 11, paddingLeft: 8 }} />
                  {keys.map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} name={k} stroke={colorFor(k, i)}
                      strokeWidth={1.8} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ThemedCard>
        );

        const AvgTable = ({ title, data, firstCol }: {
          title: string; data: { months: string[]; table: Array<{ key: string; cells: Record<string, number> }> }; firstCol: string;
        }) => (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {title}
                <span className={`ml-2 text-[10px] font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  (mean of daily values, not a sum)
                </span>
              </h3>
              <CardCountryStacked />
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    <th className="text-left py-2 font-semibold">{firstCol}</th>
                    {data.months.map(m => (
                      <th key={m} className="text-right py-2 font-semibold">{monthLabel(m)} avg DAU</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.table.map(row => (
                    <tr key={row.key} className={`border-t ${isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-700'}`}>
                      <td className="py-2">{row.key}</td>
                      {data.months.map(m => (
                        <td key={m} className="text-right py-2">{(row.cells[m] || 0).toLocaleString()}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ThemedCard>
        );

        return (
          <div className="space-y-6">
            {CC('geo-daily-countries', v => (
              <LineChartCard title={`Daily active accounts — top ${v.geoSeries.topCountries.length} countries`}
                rows={v.geoSeries.countryChart} keys={v.geoSeries.topCountries}
                colorFor={(_k, i) => SERIES_COLORS[i % SERIES_COLORS.length]} />
            ))}
            {CC('geo-avg-countries', v => (
              <AvgTable title="Average DAU by country" data={v.geoSeries.countryTable} firstCol="Country" />
            ))}

            {CC('geo-daily-regions', v => (
              <LineChartCard title={`Daily active accounts — ${v.geoSeries.regions.length} SIE regions`}
                rows={v.geoSeries.regionChart} keys={v.geoSeries.regions}
                colorFor={(k, i) => REGION_LINE_COLORS[k] || SERIES_COLORS[i % SERIES_COLORS.length]} />
            ))}
            {CC('geo-avg-regions', v => (
              <AvgTable title="Average DAU by region" data={v.geoSeries.regionTable} firstCol="Region" />
            ))}
          </div>
        );
      })()}

      <div className={`text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Note: PlayStation data has a 2-day delay. Yesterday's data is excluded. All graphs show data from December 2025.
      </div>
    </div>
    </>
    </CountryScope>
  );
}
